import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilamentFilters = ({ 
  filters, 
  onFiltersChange, 
  availableColors, 
  availableMaterials, 
  availableBrands,
  availableSuppliers,
  totalCount,
  filteredCount 
}) => {
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const handleColorFilter = (colorHex) => {
    const newColors = filters?.colors?.includes(colorHex)
      ? filters?.colors?.filter(c => c !== colorHex)
      : [...filters?.colors, colorHex];
    
    onFiltersChange({ ...filters, colors: newColors });
  };

  const handleMaterialChange = (materials) => {
    onFiltersChange({ ...filters, materials });
  };

  const handleBrandChange = (brands) => {
    onFiltersChange({ ...filters, brands });
  };

  const handleStockLevelChange = (stockLevels) => {
    onFiltersChange({ ...filters, stockLevels });
  };

  const handleSearchChange = (e) => {
    onFiltersChange({ ...filters, search: e?.target?.value });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      colors: [],
      materials: [],
      brands: [],
      suppliers: [],
      stockLevels: [],
      search: '',
      notesSearch: '',
      groupDuplicates: false,
      groupBySupplier: false
    });
  };

  const hasActiveFilters = filters?.colors?.length > 0 || 
                          filters?.materials?.length > 0 || 
                          filters?.brands?.length > 0 || 
                          filters?.suppliers?.length > 0 || 
                          filters?.stockLevels?.length > 0 || 
                          filters?.search?.length > 0 ||
                          filters?.notesSearch?.length > 0 ||
                          !!filters?.groupDuplicates ||
                          !!filters?.groupBySupplier;

  const materialOptions = [...(availableMaterials || [])]
    .sort((a, b) => a?.localeCompare(b))
    .map(material => ({ value: material, label: material }));

  const brandOptions = [...(availableBrands || [])]
    .sort((a, b) => a?.localeCompare(b))
    .map(brand => ({ value: brand, label: brand }));

  const supplierOptions = [...(availableSuppliers || [])]
    .sort((a, b) => a?.localeCompare(b))
    .map(supplier => ({ value: supplier, label: supplier }));

  const stockLevelOptions = [
    { value: 'high_g', label: 'Estoque Alto (>600g)' },
    { value: 'medium_g', label: 'Estoque Médio (200–600g)' },
    { value: 'low_g', label: 'Estoque Baixo (<200g)' }
  ];

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-foreground">Filtros</h2>
          <div className="text-sm text-muted-foreground">
            {filteredCount} de {totalCount} filamentos
          </div>
        </div>

      <div className="flex items-center space-x-2">
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} className="mr-2" />
            Limpar Filtros
          </Button>
        )}
        <div className="hidden lg:flex items-center space-x-2">
          <Checkbox
            checked={!!filters?.groupDuplicates}
            onChange={(e) => onFiltersChange({ ...filters, groupDuplicates: e?.target?.checked })}
            label="Agrupar filamentos iguais"
          />
          <Checkbox
            checked={!!filters?.groupBySupplier}
            onChange={(e) => onFiltersChange({ ...filters, groupBySupplier: e?.target?.checked })}
            label="Agrupar por fornecedor"
            disabled={!filters?.groupDuplicates}
          />
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          className="lg:hidden"
        >
          <Icon name="Filter" size={16} />
        </Button>
      </div>
      </div>

      {/* Filter Summary moved below filters for consistency with printers */}

      {/* Desktop Filters */}
      <div className="hidden lg:block space-y-6">
        {/* Search */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <Input
            type="search"
            placeholder="Buscar filamentos..."
            value={filters?.search}
            onChange={handleSearchChange}
            className="col-span-1"
            label="Buscar"
            startIconName="Search"
          />
          <Input
            type="search"
            placeholder="Buscar observações..."
            value={filters?.notesSearch || ''}
            onChange={(e) => onFiltersChange({ ...filters, notesSearch: e?.target?.value })}
            className="col-span-1"
            label="Observações"
            startIconName="Search"
          />
          
          <Select
            label={`Material (${(availableMaterials || []).length})`}
            multiple
            searchable
            clearable
            options={materialOptions}
            value={filters?.materials}
            onChange={handleMaterialChange}
          />
          
          <Select
            label={`Marca (${(availableBrands || []).length})`}
            multiple
            searchable
            clearable
            options={brandOptions}
            value={filters?.brands}
            onChange={handleBrandChange}
          />

          <Select
            label={`Fornecedor (${(availableSuppliers || []).length})`}
            multiple
            searchable
            clearable
            options={supplierOptions}
            value={filters?.suppliers || []}
            onChange={(value) => onFiltersChange({ ...filters, suppliers: value })}
          />
          
          <Select
            label="Nível de Estoque"
            multiple
            options={stockLevelOptions}
            value={filters?.stockLevels}
            onChange={handleStockLevelChange}
          />
        </div>

        {/* Color Swatches */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3">Cores ({(availableColors || [])?.length || 0})</h3>
          <div className="flex flex-wrap gap-2">
            {availableColors?.map((color) => (
              <button
                key={color?.hex}
                onClick={() => handleColorFilter(color?.hex)}
                className={`
                  w-8 h-8 rounded-full border-2 transition-all duration-200 hover:scale-110
                  ${filters?.colors?.includes(color?.hex) 
                    ? 'border-primary shadow-lg ring-2 ring-primary/20' 
                    : 'border-border hover:border-muted-foreground'
                  }
                `}
                style={{ backgroundColor: color?.hex }}
                title={color?.name}
              />
            ))}
          </div>
        </div>
      </div>
      {/* Mobile Filters */}
      {showMobileFilters && (
        <div className="lg:hidden space-y-4 border-t border-border pt-4">
          <Input
            type="search"
            placeholder="Buscar filamentos..."
            value={filters?.search}
            onChange={handleSearchChange}
            label="Buscar"
            startIconName="Search"
          />
          <Input
            type="search"
            placeholder="Buscar observações..."
            value={filters?.notesSearch || ''}
            onChange={(e) => onFiltersChange({ ...filters, notesSearch: e?.target?.value })}
            label="Observações"
            startIconName="Search"
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label={`Material (${(availableMaterials || []).length})`}
              multiple
              searchable
              clearable
              options={materialOptions}
              value={filters?.materials}
              onChange={handleMaterialChange}
            />
            
            <Select
              label={`Marca (${(availableBrands || []).length})`}
              multiple
              searchable
              clearable
              options={brandOptions}
              value={filters?.brands}
              onChange={handleBrandChange}
            />
          </div>

          <Select
            label={`Fornecedor (${(availableSuppliers || []).length})`}
            multiple
            searchable
            clearable
            options={supplierOptions}
            value={filters?.suppliers || []}
            onChange={(value) => onFiltersChange({ ...filters, suppliers: value })}
          />
          
          <Select
            label="Nível de Estoque"
            multiple
            options={stockLevelOptions}
            value={filters?.stockLevels}
            onChange={handleStockLevelChange}
          />

          <div className="flex items-center">
            <Checkbox
              checked={!!filters?.groupDuplicates}
              onChange={(e) => onFiltersChange({ ...filters, groupDuplicates: e?.target?.checked })}
              label="Agrupar filamentos iguais"
            />
            <div className="ml-4">
              <Checkbox
                checked={!!filters?.groupBySupplier}
                onChange={(e) => onFiltersChange({ ...filters, groupBySupplier: e?.target?.checked })}
                label="Agrupar por fornecedor"
                disabled={!filters?.groupDuplicates}
              />
            </div>
          </div>

          {/* Mobile Color Swatches */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Cores ({(availableColors || [])?.length || 0})</h3>
            <div className="flex flex-wrap gap-2">
              {availableColors?.map((color) => (
                <button
                  key={color?.hex}
                  onClick={() => handleColorFilter(color?.hex)}
                  className={`
                    w-8 h-8 rounded-full border-2 transition-all duration-200
                    ${filters?.colors?.includes(color?.hex) 
                      ? 'border-primary shadow-lg ring-2 ring-primary/20' 
                      : 'border-border'
                    }
                  `}
                  style={{ backgroundColor: color?.hex }}
                  title={color?.name}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter Summary (below filters, consistent with printers) */}
      {hasActiveFilters && (
        <div className="mt-6 p-4 bg-muted/30 rounded-xl border border-border/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-foreground">Filtros Ativos</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={12} className="mr-1" />
              Limpar Todos
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {filters?.search && (
              <div className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary border border-primary/20 rounded-full text-sm">
                <Icon name="Search" size={12} />
                <span>"{filters.search}"</span>
                <button
                  onClick={() => onFiltersChange({ ...filters, search: '' })}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            )}
            {filters?.notesSearch && (
              <div className="flex items-center space-x-1 px-3 py-1 bg-secondary/10 text-secondary border border-secondary/20 rounded-full text-sm">
                <Icon name="Notebook" size={12} />
                <span>obs: "{filters.notesSearch}"</span>
                <button
                  onClick={() => onFiltersChange({ ...filters, notesSearch: '' })}
                  className="ml-1 hover:bg-secondary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            )}
            {filters?.materials?.map(material => (
              <div key={material} className="flex items-center space-x-1 px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm">
            <Icon name="Filament" size={12} />
                <span>{material}</span>
                <button
                  onClick={() => onFiltersChange({ ...filters, materials: filters.materials.filter(m => m !== material) })}
                  className="ml-1 hover:bg-secondary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            ))}
            {filters?.brands?.map(brand => (
              <div key={brand} className="flex items-center space-x-1 px-3 py-1 bg-black/50 text-white border border-white/30 shadow-xs backdrop-blur-sm rounded-full text-sm">
                <Icon name="Tag" size={12} />
                <span>{brand}</span>
                <button
                  onClick={() => onFiltersChange({ ...filters, brands: filters.brands.filter(b => b !== brand) })}
                  className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            ))}
            {filters?.suppliers?.map(supplier => (
              <div key={supplier} className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary border border-primary/20 rounded-full text-sm">
                <Icon name="Tag" size={12} />
                <span>{supplier}</span>
                <button
                  onClick={() => onFiltersChange({ ...filters, suppliers: filters.suppliers.filter(s => s !== supplier) })}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            ))}
            {filters?.stockLevels?.map(level => {
              const levelLabel = stockLevelOptions.find(opt => opt.value === level)?.label || level;
              return (
                <div key={level} className="flex items-center space-x-1 px-3 py-1 bg-warning/10 text-warning rounded-full text-sm">
                  <Icon name="Gauge" size={12} />
                  <span>{levelLabel}</span>
                  <button
                    onClick={() => onFiltersChange({ ...filters, stockLevels: filters.stockLevels.filter(l => l !== level) })}
                    className="ml-1 hover:bg-warning/20 rounded-full p-0.5"
                  >
                    <Icon name="X" size={10} />
                  </button>
                </div>
              );
            })}
            {filters?.colors?.map(colorHex => {
              const colorData = availableColors?.find(c => c.hex === colorHex);
              return (
                <div key={colorHex} className="flex items-center space-x-2 px-3 py-1 bg-muted/50 rounded-full text-sm">
                  <div 
                    className="w-3 h-3 rounded-full border border-border"
                    style={{ backgroundColor: colorHex }}
                  />
                  <span>{colorData?.name || 'Cor'}</span>
                  <button
                    onClick={() => onFiltersChange({ ...filters, colors: filters.colors.filter(c => c !== colorHex) })}
                    className="ml-1 hover:bg-muted/70 rounded-full p-0.5"
                  >
                    <Icon name="X" size={10} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilamentFilters;